import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  AppsOutlined,
  ArrowForwardOutlined,
  LockOutlined,
  CancelOutlined,
} from '@mui/icons-material';
import {
  CARD_SX,
  SECTION_ICON_BOX_SX,
  formatCurrency,
  formatDate,
} from './accountOverview.utils';

export default function AccountOverviewProductsPanel({
  productsBreakdown,
  pendingCancellationByProductKey,
  isOwner,
  statusKey,
  cancelAtPeriodEnd,
  cancellingProductKey,
  navigate,
  getProductEntryPath,
  openCancelProductDialog,
  setCheckoutProductKey,
}) {
  return (
<Box sx={{ ...CARD_SX, p: 0 }}>
                <Box
                  sx={{
                    px: { xs: 2.75, md: 3 },
                    py: 2.25,
                    borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={1.5}>
                    <Box sx={SECTION_ICON_BOX_SX}>
                      <AppsOutlined sx={{ fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em', lineHeight: 1.2 }}
                      >
                        Seus produtos
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Visão geral dos produtos contratados e dos que podem ser ativados.
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" gap={0.75} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={`${productsBreakdown.counts.available} ${productsBreakdown.counts.available === 1 ? 'ativo' : 'ativos'}`}
                      sx={{
                        background: 'linear-gradient(135deg, rgba(16,185,129,0.16), rgba(5,150,105,0.10))',
                        color: '#047857',
                        border: '1px solid rgba(16,185,129,0.32)',
                        fontWeight: 600,
                        borderRadius: '8px',
                      }}
                    />
                    <Chip
                      size="small"
                      label={`${productsBreakdown.counts.comingSoon} em breve`}
                      sx={{
                        background: 'rgba(245, 158, 11, 0.14)',
                        color: '#92400e',
                        border: '1px solid rgba(245, 158, 11, 0.30)',
                        fontWeight: 600,
                        borderRadius: '8px',
                      }}
                    />
                    <Chip
                      size="small"
                      label={`${productsBreakdown.counts.blocked} disponíveis para ativação`}
                      sx={{
                        background: 'rgba(8, 145, 178, 0.10)',
                        color: '#0891b2',
                        border: '1px solid rgba(8, 145, 178, 0.32)',
                        fontWeight: 600,
                        borderRadius: '8px',
                      }}
                    />
                  </Stack>
                </Box>
                <Box
                  sx={{
                    p: { xs: 1.5, md: 2 },
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1.5,
                  }}
                >
                  {productsBreakdown.items.map((product) => {
                    const isAvailable = product.status === 'available';
                    const isComingSoon = product.status === 'coming_soon';
                    const pendingCancellation = pendingCancellationByProductKey[product.key];
                    const canCancelProduct =
                      isOwner
                      && isAvailable
                      && product.isBillableActive
                      && !product.includedByAtendabot
                      && !pendingCancellation
                      && statusKey !== 'canceled'
                      && !cancelAtPeriodEnd;
                    const productCancelDisabled = Boolean(cancellingProductKey);
                    const openProduct = () => navigate(getProductEntryPath(product.key, product.link));
                    const billingLine = product.billingLine;
                    const productPriceLabel = product.includedByAtendabot
                      ? 'Incluso no AtendaBot'
                      : billingLine
                        ? `${formatCurrency(billingLine.discountedUnitAmount, 'brl')}/mês${
                            Number(billingLine.discountAmount) > 0
                              ? ` (${formatCurrency(billingLine.discountAmount, 'brl')} de desconto)`
                              : ''
                          }`
                        : null;
                    return (
                      <Box
                        key={product.key}
                        sx={{
                          p: 2,
                          borderRadius: '14px',
                          border: '1px solid rgba(15, 23, 42, 0.08)',
                          background: isAvailable
                            ? 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)'
                            : 'rgba(15, 23, 42, 0.015)',
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) auto',
                          alignItems: 'center',
                          gap: 2,
                          cursor: 'default',
                          transition: 'all 0.2s ease',
                          opacity: isAvailable ? 1 : 0.78,
                          '&:hover': isAvailable
                            ? {
                                borderColor: 'rgba(8, 145, 178, 0.32)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 12px 28px -12px rgba(8, 145, 178, 0.30)',
                              }
                            : {},
                          '&:hover .arrow-icon': isAvailable
                            ? { transform: 'translateX(4px)', color: '#0891b2' }
                            : {},
                        }}
                      >
                        <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              flexShrink: 0,
                              ...(isAvailable
                                ? {
                                    bgcolor: '#10b981',
                                    boxShadow: '0 0 0 4px rgba(16,185,129,0.18)',
                                  }
                                : isComingSoon
                                  ? {
                                      bgcolor: '#f59e0b',
                                      boxShadow: '0 0 0 4px rgba(245,158,11,0.18)',
                                    }
                                  : {
                                      bgcolor: '#94a3b8',
                                      boxShadow: '0 0 0 4px rgba(148,163,184,0.18)',
                                    }),
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: '#0f172a',
                                fontSize: '0.95rem',
                                letterSpacing: '-0.01em',
                              }}
                            >
                              {product.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#64748b',
                                fontSize: '0.82rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {product.tagline}
                            </Typography>
                            {isAvailable && productPriceLabel && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: product.includedByAtendabot ? '#047857' : '#0f766e',
                                  fontSize: '0.74rem',
                                  fontWeight: 700,
                                  mt: 0.35,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {productPriceLabel}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                        <Stack
                          direction="column"
                          alignItems="flex-end"
                          justifyContent="center"
                          gap={0.75}
                          sx={{ justifySelf: 'end', minWidth: 96 }}
                        >
                          {isAvailable ? (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                endIcon={<ArrowForwardOutlined className="arrow-icon" sx={{ fontSize: 16 }} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openProduct();
                                }}
                                sx={{
                                  borderRadius: '8px',
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  fontSize: '0.72rem',
                                  px: 1.25,
                                  py: 0.38,
                                  whiteSpace: 'nowrap',
                                  bgcolor: '#0891b2',
                                  boxShadow: 'none',
                                  width: '100%',
                                  '&:hover': {
                                    bgcolor: '#0e7490',
                                    boxShadow: '0 8px 18px -10px rgba(8,145,178,0.7)',
                                  },
                                }}
                              >
                                Acessar
                              </Button>
                              {product.promptOnly && isOwner ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCheckoutProductKey(product.key);
                                  }}
                                  sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.72rem',
                                    px: 1.25,
                                    py: 0.35,
                                    whiteSpace: 'nowrap',
                                    width: '100%',
                                    borderColor: 'rgba(8, 145, 178, 0.32)',
                                    color: '#0891b2',
                                    '&:hover': {
                                      borderColor: '#0891b2',
                                      background: 'rgba(8, 145, 178, 0.06)',
                                    },
                                  }}
                                >
                                  Ativar
                                </Button>
                              ) : canCancelProduct ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={<CancelOutlined sx={{ fontSize: 15 }} />}
                                  disabled={productCancelDisabled}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openCancelProductDialog(product);
                                  }}
                                  sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.72rem',
                                    px: 1.25,
                                    py: 0.35,
                                    whiteSpace: 'nowrap',
                                    width: '100%',
                                    color: '#b91c1c',
                                    '&:hover': {
                                      background: 'rgba(220, 38, 38, 0.06)',
                                    },
                                  }}
                                >
                                  {cancellingProductKey === product.key ? 'Cancelando...' : 'Cancelar'}
                                </Button>
                              ) : (
                                <Chip
                                  size="small"
                                  label={
                                    pendingCancellation
                                      ? `Ativo até ${formatDate(pendingCancellation.effective_at)}`
                                      : product.includedByAtendabot
                                        ? 'Incluso'
                                        : 'Ativo'
                                  }
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                    borderRadius: '8px',
                                    background:
                                      'linear-gradient(135deg, rgba(16,185,129,0.16), rgba(5,150,105,0.10))',
                                    color: '#047857',
                                    border: '1px solid rgba(16,185,129,0.32)',
                                    width: '100%',
                                  }}
                                />
                              )}
                            </>
                          ) : isComingSoon ? (
                            <>
                              <Chip
                                size="small"
                                label="Em breve"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  borderRadius: '8px',
                                  background: 'rgba(245, 158, 11, 0.14)',
                                  color: '#92400e',
                                  border: '1px solid rgba(245, 158, 11, 0.30)',
                                }}
                              />
                              <LockOutlined sx={{ fontSize: 16, color: '#94a3b8', opacity: 0.6 }} />
                            </>
                          ) : isOwner ? (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCheckoutProductKey(product.key);
                              }}
                              sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                px: 1.5,
                                py: 0.4,
                                borderColor: 'rgba(8, 145, 178, 0.32)',
                                color: '#0891b2',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                  borderColor: '#0891b2',
                                  background: 'rgba(8, 145, 178, 0.06)',
                                },
                              }}
                            >
                              Ativar
                            </Button>
                          ) : (
                            <LockOutlined sx={{ fontSize: 16, color: '#94a3b8', opacity: 0.7 }} />
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
  );
}
